import {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
  type RefObject,
} from 'react';
import type { LayoutChangeEvent } from 'react-native';

import {
  AtomicHeaderType,
  type IScreensComponent,
  type ScreensReadModel,
} from '../../api';
import {
  getScreenComponent,
  convertScreenComponent,
} from '../../api/screensService/getScreenComponent';
import { Flex, List, type ListRef, useSafeArea } from '@banhala/ui-kit';
import { delay, isNil, isNotNil, isWeb } from '../../utils';
import Animated, {
  type SharedValue,
  useAnimatedScrollHandler,
  useSharedValue,
} from 'react-native-reanimated';
import { scheduleOnRN } from 'react-native-worklets';
import { ScrollToTopButton } from './ScrollToTopButton';
import {
  useLikeMap,
  useOutfitBookmarkMap,
  useAddLikeGoods,
  useGrowthBookFeatureValue,
} from '../../hooks';
import { AtomicComponent } from './utils/createAtomicComponent';
import { ThreeColGoodsRow } from './ItemList/ThreeColGoodsList/ThreeColGoodsRow';
import { TwoColGoodsRow } from './ItemList/TwoColGoodsList/TwoColGoodsRow';
import { GoodsCardSkeletonList } from '@banhala/ui-kit';
import type { RowItem } from './types';
import {
  useFlattenedItems,
  FLATTEN_ITEM_TYPES,
  type FlattenedItem,
} from './useFlattenedItems';
import { bridge, useNavigator } from '../../utils';
import { useRemoveLikeGoods } from '../../queries';
import { AdAction } from '../../utils/eventLogger/adLogger/types';

export interface AtomicRendererProps {
  screen?: string;
  logDisabled?: boolean;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any -- 외부 ListRef<IScreensComponent>와 내부 List<FlattenedItem> 간 제네릭 불일치로 any 불가피
  listRef: RefObject<ListRef<any> | null>;
  data: ScreensReadModel;
  fetchNextPage: VoidFunction;
  filterListIndex?: number;
  scrollToFilterList: VoidFunction;
  isFetching: boolean;
  refetch: VoidFunction;
  hideScrollToTop?: boolean;
  onScrollY?: (scrollY: number) => void;
  scrollYSharedValue?: SharedValue<number>;
  onBeginDrag?: (scrollY: number) => void;
  onEndDrag?: (scrollY: number) => void;
  itemHeightsRef?: RefObject<{ [key: number]: number }>;
  onItemHeightChange?: (index: number) => void;
}

const AnimatedList = Animated.createAnimatedComponent(List<FlattenedItem>);
const VIEWABILITY_CONFIG = { itemVisiblePercentThreshold: 50 };

/**
 * 서버에서 받은 Atomic 컴포넌트 배열을 렌더링하는 메인 리스트.
 *
 * 2col/3col GoodsList/CardList는 내부에 수직 List를 가져서
 * 수직×수직 중첩이 발생하므로, 행 단위로 평탄화하여 중첩을 제거한다.
 * 나머지 컴포넌트(Header, FilterList, Divider 등)는 원본 그대로 렌더링한다.
 */
export const AtomicRenderer = ({
  listRef,
  data,
  fetchNextPage,
  filterListIndex,
  scrollToFilterList,
  isFetching,
  refetch,
  screen,
  hideScrollToTop,
  onScrollY,
  scrollYSharedValue,
  onBeginDrag,
  onEndDrag,
  itemHeightsRef,
  onItemHeightChange,
  logDisabled,
}: AtomicRendererProps) => {
  const atomicRendererScrollY = useSharedValue(0);
  const [isRefreshing, setIsRefreshing] = useState(false);
  const prevContentSizeHeightRef = useRef<number>(0);
  const layoutRef = useRef<{ height: number }>({ height: 0 });
  const isFetchingNextPageRef = useRef(false);

  const internalItemHeightsRef = useRef<{ [key: number]: number }>({});
  const itemHeights = itemHeightsRef ?? internalItemHeightsRef;
  const { likeMap, setLikeMap } = useLikeMap();
  const { bookmarkMap, setBookmarkMap } = useOutfitBookmarkMap({
    initialMap: {},
  });

  const [componentOverrides, setComponentOverrides] = useState<
    Record<number, IScreensComponent>
  >({});

  const debouncedFetchNextPage = useCallback(() => {
    if (isFetchingNextPageRef.current) return;
    isFetchingNextPageRef.current = true;
    fetchNextPage();
  }, [fetchNextPage]);

  useEffect(() => {
    if (!isFetching) {
      isFetchingNextPageRef.current = false;
    }
  }, [isFetching]);

  useEffect(() => {
    setComponentOverrides({});
  }, [data.components]);

  const effectiveComponents = useMemo(
    () => data.components.map(c => componentOverrides[c.componentId] ?? c),
    [data.components, componentOverrides],
  );

  // 평탄화 피처플래그 (GrowthBook)
  // https://growthbook.ablycorp.com/features/rn_flattened_items
  // 웹에서는 GrowthBook 브릿지 미지원 → 항상 평탄화 활성
  const growthBookFlag =
    useGrowthBookFeatureValue<boolean>('rn_flattened_items') ?? false;
  const isFlattenEnabled = isWeb || growthBookFlag;

  const flattenedItems = useFlattenedItems(
    effectiveComponents,
    isFlattenEnabled,
  );

  // 페이지네이션 로딩 시 스켈레톤 행 추가 (평탄화 여부 무관)
  const SKELETON_ROW_COUNT = 2;
  const displayItems = useMemo<FlattenedItem[]>(() => {
    if (!isFetching) return flattenedItems;

    // 평탄화 ON: flattenedItems에서 마지막 행 타입으로 판단
    const lastRowKind = [...flattenedItems]
      .reverse()
      .find(fi => fi.kind === 'row-3col' || fi.kind === 'row-2col')?.kind;

    // 평탄화 OFF: 서버 컴포넌트의 마지막 itemList 타입으로 판단
    const lastComponentType = !lastRowKind
      ? [...effectiveComponents]
          .reverse()
          .find(c => c.type.itemList && FLATTEN_ITEM_TYPES.has(c.type.itemList))
          ?.type.itemList
      : undefined;

    const is2col =
      lastRowKind === 'row-2col' ||
      lastComponentType === 'TWO_COL_GOODS_LIST' ||
      lastComponentType === 'TWO_COL_CARD_LIST';

    const skeletons: FlattenedItem[] = Array.from(
      { length: SKELETON_ROW_COUNT },
      (_, i) =>
        is2col
          ? { kind: 'skeleton-2col' as const, key: `skeleton-${i}` }
          : { kind: 'skeleton-3col' as const, key: `skeleton-${i}` },
    );

    return [...flattenedItems, ...skeletons];
  }, [flattenedItems, isFetching, effectiveComponents]);

  // 평탄화된 상품 행의 좋아요/프레스 핸들러
  const { navigator } = useNavigator();
  const { mutateAsync: mutateRemoveLikeGoods } = useRemoveLikeGoods();
  const firstFlattenTarget = useMemo(() => {
    const target = effectiveComponents.find(
      c => c.type.itemList && FLATTEN_ITEM_TYPES.has(c.type.itemList),
    );
    return target?.entity.itemList?.[0];
  }, [effectiveComponents]);

  const screenName = useMemo(() => {
    return (
      screen ??
      (firstFlattenTarget?.logging?.analytics?.SCREEN as string | undefined) ??
      ''
    );
  }, [firstFlattenTarget, screen]);

  const { addLikeGoods } = useAddLikeGoods({
    listParams: firstFlattenTarget?.logging?.analytics?.LIST_ID as
      | string
      | undefined,
    onChangeIsLiked: (sno, nextValue) => {
      setLikeMap(prevState => ({ ...prevState, [sno]: nextValue }));
    },
    screen: screenName,
  });

  // 평탄화된 상품의 likeMap 초기화
  useEffect(() => {
    for (const component of effectiveComponents) {
      if (
        !component.type.itemList ||
        !FLATTEN_ITEM_TYPES.has(component.type.itemList)
      )
        continue;
      const entity = component.entity.itemList;
      if (!entity) continue;
      setLikeMap(prev => ({
        ...prev,
        ...entity.reduce<Record<string, boolean>>(
          (
            acc,
            cur: { item?: { sno?: number; like?: { is_liked?: boolean } } },
          ) => {
            if (cur.item?.sno != null) {
              acc[cur.item.sno] = cur.item.like?.is_liked ?? false;
            }
            return acc;
          },
          {},
        ),
      }));
    }
  }, [effectiveComponents, setLikeMap]);

  const handlePressGoodsItem = useCallback(
    (item: RowItem) => {
      // 일반 상품 카드
      if (item.type === 'goods') {
        if (item.logging) {
          bridge.logAnalyticsEvent({
            name: 'CLICK_GOODS',
            analytics: {
              ...(isNotNil(screen) && { SCREEN: screen }),
              ...item.logging.analytics,
            },
            ably_only_log: item.logging.ably_only_log,
          });
        }
        if (isNotNil(item.ad)) {
          bridge.logAdEvent({ action: AdAction.CLICK, ad: item.ad });
        }
        navigator.moveToGoods({
          sno: item.sno,
          remoteDeeplink: item.remoteDeeplink,
          firstPageRendering: item.firstPageRendering ?? null,
          loggingAnalytics: item.logging?.analytics as
            | Record<string, unknown>
            | undefined,
        });
        return;
      }

      // 비디오 상품 카드 (VIDEO_GOODS_CARD / FULL_VIDEO_GOODS_CARD)
      if (item.type === 'videoGoods') {
        bridge.logAnalyticsEvent({
          name: 'CLICK_GOODS',
          analytics: {
            CARD_TYPE: 'VIDEO',
            ...item.logging.analytics,
            ...(isNotNil(screen) && { SCREEN: screen }),
          },
          ably_only_log: item.logging.ably_only_log,
        });
        if (isNotNil(item.item.ad)) {
          bridge.logAdEvent({ action: AdAction.CLICK, ad: item.item.ad });
        }
        navigator.moveToGoods({
          sno: item.sno,
          firstPageRendering: item.item.first_page_rendering ?? null,
          loggingAnalytics: item.logging.analytics as
            | Record<string, unknown>
            | undefined,
        });
        return;
      }
    },
    [navigator, screen],
  );

  const handlePressLike = useCallback(
    async (item: RowItem) => {
      // 일반 상품 카드
      if (item.type === 'goods') {
        const currentLike = likeMap[item.sno] ?? false;
        if (item.logging) {
          bridge.logAnalyticsEvent({
            name: currentLike ? 'CLICK_UNLIKE_GOODS' : 'CLICK_LIKE_GOODS',
            analytics: item.logging.analytics,
            ably_only_log: item.logging.ably_only_log,
          });
        }
        if (isNotNil(item.ad)) {
          bridge.logAdEvent({
            action: currentLike ? AdAction.UNLIKE : AdAction.LIKE,
            ad: item.ad,
          });
        }
        if (currentLike) {
          await mutateRemoveLikeGoods({
            sno: item.sno,
            list_params_referrer: item.listId,
          });
          bridge.likeGoods({ goods_sno: item.sno, is_liked: false });
          setLikeMap(prev => ({ ...prev, [item.sno]: false }));
          return;
        }
        await addLikeGoods(
          item.sno,
          item.logging?.analytics,
          item.logging?.ably_only_log,
        );
        return;
      }

      // 비디오 상품 카드 (VIDEO_GOODS_CARD / FULL_VIDEO_GOODS_CARD)
      if (item.type === 'videoGoods') {
        const currentLike = likeMap[item.sno] ?? false;
        bridge.logAnalyticsEvent({
          name: currentLike ? 'CLICK_UNLIKE_GOODS' : 'CLICK_LIKE_GOODS',
          analytics: {
            CARD_TYPE: 'VIDEO',
            ...item.logging.analytics,
          },
          ably_only_log: item.logging.ably_only_log,
        });
        if (isNotNil(item.item.ad)) {
          bridge.logAdEvent({
            action: currentLike ? AdAction.UNLIKE : AdAction.LIKE,
            ad: item.item.ad,
          });
        }
        if (currentLike) {
          await mutateRemoveLikeGoods({
            sno: item.sno,
            list_params_referrer: item.item.like?.list_id,
          });
          bridge.likeGoods({ goods_sno: item.sno, is_liked: false });
          setLikeMap(prev => ({ ...prev, [item.sno]: false }));
          return;
        }
        await addLikeGoods(
          item.sno,
          item.logging.analytics,
          item.logging.ably_only_log,
        );
        return;
      }
    },
    [likeMap, mutateRemoveLikeGoods, setLikeMap, addLikeGoods],
  );

  // 평탄화된 행의 display 로깅
  const displayLoggedRef = useRef<Set<string>>(new Set());
  // 뷰포트에 보이는 component-kind 항목의 componentId 추적
  // → 배너의 autoScroll gate용. 스크롤 리렌더 억제를 위해 실제 변경 시에만 setState.
  const visibleComponentIdsRef = useRef<Set<number>>(new Set());
  const [visibleComponentIds, setVisibleComponentIds] = useState<Set<number>>(
    () => new Set(),
  );
  const handleViewableItemsChanged = useCallback(
    ({
      viewableItems,
    }: {
      viewableItems: Array<{ item: FlattenedItem; isViewable: boolean }>;
    }) => {
      // 가시성 Set 갱신 — logDisabled와 무관하게 항상 수행 (autoScroll gate 목적)
      const nextVisibleIds = new Set<number>();
      for (const { item, isViewable } of viewableItems) {
        if (!isViewable) continue;
        if (item.kind === 'component') {
          nextVisibleIds.add(item.component.componentId);
        }
      }
      const prevIds = visibleComponentIdsRef.current;
      let visibilityChanged = nextVisibleIds.size !== prevIds.size;
      if (!visibilityChanged) {
        for (const id of nextVisibleIds) {
          if (!prevIds.has(id)) {
            visibilityChanged = true;
            break;
          }
        }
      }
      if (visibilityChanged) {
        visibleComponentIdsRef.current = nextVisibleIds;
        setVisibleComponentIds(nextVisibleIds);
      }

      if (logDisabled) return;

      for (const { item, isViewable } of viewableItems) {
        if (!isViewable) continue;
        if (item.kind !== 'row-3col' && item.kind !== 'row-2col') continue;

        for (const rowItem of item.items) {
          if (rowItem.type === 'goods' && rowItem.logging) {
            const logKey = `goods-${rowItem.sno}`;
            if (displayLoggedRef.current.has(logKey)) continue;
            displayLoggedRef.current.add(logKey);

            bridge.logAnalyticsEvent({
              name: 'DISPLAY_GOODS',
              analytics: {
                ...(isNotNil(screen) && { SCREEN: screen }),
                ad: rowItem.ad,
                ...rowItem.logging.analytics,
              },
              ably_only_log: rowItem.logging.ably_only_log,
            });

            if (isNotNil(rowItem.ad)) {
              bridge.logAdEvent({
                action: AdAction.DISPLAY,
                ad: rowItem.ad,
              });
            }
          } else if (rowItem.type === 'videoGoods') {
            const logKey = `video-${rowItem.sno}`;
            if (displayLoggedRef.current.has(logKey)) continue;
            displayLoggedRef.current.add(logKey);

            bridge.logAnalyticsEvent({
              name: 'DISPLAY_GOODS',
              analytics: {
                CARD_TYPE: 'VIDEO',
                ...rowItem.logging.analytics,
                ...(isNotNil(screen) && { SCREEN: screen }),
              },
              ably_only_log: rowItem.logging.ably_only_log,
            });

            if (isNotNil(rowItem.item.ad)) {
              bridge.logAdEvent({
                action: AdAction.DISPLAY,
                ad: rowItem.item.ad,
              });
            }
          } else if (rowItem.type === 'primaryCard') {
            const logKey = `primary-${rowItem.key}`;
            if (displayLoggedRef.current.has(logKey)) continue;
            displayLoggedRef.current.add(logKey);

            bridge.logAnalyticsEvent({
              name: 'DISPLAY_PRIMARY_CARD',
              analytics: rowItem.logging.analytics,
              ably_only_log: rowItem.logging.ably_only_log,
            });
          }
        }
      }
    },
    [logDisabled, screen],
  );

  const handleReloadComponent = useCallback(
    async (
      componentId: number,
      carouselSno?: number,
      queryParams?: Record<string, string>,
    ) => {
      try {
        const rawComponent = await getScreenComponent({
          screenName: data.screenName,
          componentId,
          prevCarousel: carouselSno,
          queryParams,
        });
        const converted = convertScreenComponent(rawComponent, data.logging);
        setComponentOverrides(prev => ({
          ...prev,
          [componentId]: converted,
        }));
      } catch {
        // 네트워크 에러 시 무시 — 기존 컴포넌트 유지
      }
    },
    [data.screenName, data.logging],
  );

  const onScrollToIndexFailed = async (info: { index: number }) => {
    await delay(300);
    listRef.current?.scrollToIndex({
      index: info.index,
      animated: false,
    });
  };

  const handleScroll = useAnimatedScrollHandler({
    onScroll: ({ contentSize, layoutMeasurement, contentOffset }) => {
      atomicRendererScrollY.value = contentOffset.y;

      if (scrollYSharedValue) {
        scrollYSharedValue.value = contentOffset.y;
      }

      if (onScrollY) {
        scheduleOnRN(onScrollY, contentOffset.y);
      }

      if (
        isFetching ||
        prevContentSizeHeightRef.current === contentSize.height
      ) {
        return;
      }
      const distanceFromBottom =
        contentSize.height - layoutMeasurement.height - contentOffset.y;
      if (distanceFromBottom < layoutMeasurement.height * 3) {
        // FlatList가 화면을 그리기전에 scroll을 하면 계속 fetchNextPage를 호출하여 이를 방지.
        // 앱에서만 해당 문제가 발생
        prevContentSizeHeightRef.current = contentSize.height;
        scheduleOnRN(debouncedFetchNextPage);
      }
    },
    onBeginDrag: ({ contentOffset }) => {
      if (onBeginDrag) {
        scheduleOnRN(onBeginDrag, contentOffset.y);
      }
    },
    onEndDrag: ({ contentOffset }) => {
      if (onEndDrag) {
        scheduleOnRN(onEndDrag, contentOffset.y);
      }
    },
  });

  // stickyHeaderIndices: flattenedItems 내에서 FilterList 위치 계산
  const stickyHeaderIndices = useMemo(() => {
    if (isNil(filterListIndex)) return undefined;
    const idx = flattenedItems.findIndex(
      fi => fi.kind === 'component' && fi.originalIndex === filterListIndex,
    );
    return idx >= 0 ? [idx] : undefined;
  }, [filterListIndex, flattenedItems]);

  const onRefresh = () => {
    setIsRefreshing(true);
    // TODO: refetch를 할 때 pagination한만큼의 api를 모두 호출하는 이슈가 있음
    refetch();
    setIsRefreshing(false);
  };

  const onLayout = (e: LayoutChangeEvent) => {
    layoutRef.current.height = e.nativeEvent.layout.height;
  };

  const onContentSizeChange = (_width: number, height: number) => {
    if (isFetching || isNil(data.nextToken)) {
      return;
    }
    // 컨텐츠가 화면보다 작으면 다음 페이지 로드
    if (height < layoutRef.current.height) {
      debouncedFetchNextPage();
    }
  };
  const { bottom } = useSafeArea();

  return (
    <>
      <AnimatedList
        ref={listRef}
        data={displayItems}
        showsVerticalScrollIndicator={false}
        stickyHeaderIndices={stickyHeaderIndices}
        onScroll={handleScroll}
        scrollEventThrottle={16}
        contentContainerStyle={{ paddingBottom: bottom }}
        keyExtractor={item => item.key}
        renderItem={({ item, index }) => {
          if (item.kind === 'skeleton-3col') {
            return (
              <GoodsCardSkeletonList
                count={3}
                numColumns={3}
                ratio={1.2}
                bottomContentHeight={60}
              />
            );
          }
          if (item.kind === 'skeleton-2col') {
            return (
              <GoodsCardSkeletonList
                count={2}
                numColumns={2}
                ratio={1.2}
                bottomContentHeight={60}
              />
            );
          }
          if (item.kind === 'row-3col') {
            return (
              <ThreeColGoodsRow
                items={item.items}
                onPressItem={handlePressGoodsItem}
                onPressLike={handlePressLike}
                likeMap={likeMap}
              />
            );
          }
          if (item.kind === 'row-2col') {
            return (
              <TwoColGoodsRow
                items={item.items}
                onPressItem={handlePressGoodsItem}
                onPressLike={handlePressLike}
                likeMap={likeMap}
              />
            );
          }

          const comp = item.component;
          const onReload =
            comp.type.header === AtomicHeaderType.HeadlineReloadOneLine
              ? () =>
                  handleReloadComponent(
                    comp.componentId,
                    comp.entity.header?.item.carousel_sno,
                  )
              : undefined;

          return (
            <Flex
              onLayout={e => {
                const newHeight = e.nativeEvent.layout.height;
                const prevHeight = itemHeights.current[index];
                itemHeights.current[index] = newHeight;
                if (isNil(prevHeight) || prevHeight !== newHeight) {
                  onItemHeightChange?.(index);
                }
              }}
            >
              <AtomicComponent
                component={comp}
                index={item.originalIndex}
                isVisible={visibleComponentIds.has(comp.componentId)}
                likeMap={likeMap}
                setLikeMap={setLikeMap}
                bookmarkMap={bookmarkMap}
                setBookmarkMap={setBookmarkMap}
                scrollToFilterList={scrollToFilterList}
                screen={screen}
                screenName={data.screenName}
                screenLogging={data.logging}
                onReload={onReload}
                onReloadWithParams={(queryParams: Record<string, string>) =>
                  handleReloadComponent(
                    comp.componentId,
                    undefined,
                    queryParams,
                  )
                }
                logDisabled={logDisabled}
              />
            </Flex>
          );
        }}
        onViewableItemsChanged={handleViewableItemsChanged}
        viewabilityConfig={VIEWABILITY_CONFIG}
        refreshing={isRefreshing}
        onRefresh={onRefresh}
        disableVirtualization={isWeb}
        onScrollToIndexFailed={onScrollToIndexFailed}
        onLayout={onLayout}
        onContentSizeChange={onContentSizeChange}
      />
      {!hideScrollToTop && (
        <ScrollToTopButton
          atomicRendererScrollY={atomicRendererScrollY}
          listRef={listRef}
        />
      )}
    </>
  );
};
